/**
 * UpgradePlanDialog — OA plan-upgrade request (iter-2026 Phase 2).
 * Marketing-styled popup shown only to Standard-plan orgs. On submit,
 * posts to /billing/plans/request-upgrade which emails the Analyzio team
 * with the org name, current plan and requesting Org Admin.
 */
import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Rocket, Check, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const BENEFITS = [
    "Unlimited AI credits — never hit a wall mid-analysis",
    "Unlimited users, dashboards & workspaces",
    "SSO / SAML, advanced governance & audit controls",
    "Priority support with a dedicated success manager",
];

export function UpgradePlanDialog({ open, onOpenChange, currentPlanName = "Standard" }) {
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [legalLinks, setLegalLinks] = useState({
        terms: { url: "/terms", is_pdf: false },
        privacy: { url: "/privacy", is_pdf: false },
    });

    // Reuse the SA-configured Terms/Privacy documents (same source as signup).
    useEffect(() => {
        if (!open) return;
        api.get("/legal/links").then((r) => {
            if (r.data && r.data.terms && r.data.privacy) setLegalLinks(r.data);
        }).catch(() => { });
    }, [open]);

    // PDF/legal URLs may be API-relative — prefix the backend origin so the
    // document opens directly in a new tab.
    const linkHref = (l, fallback) => {
        const u = (l && l.url) || fallback;
        return u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${u}` : u;
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            await api.post("/billing/plans/request-upgrade", { note, accepted_terms: true });
            toast.success("Upgrade request sent. Our team will be in touch soon.");
            setNote("");
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send request");
        } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
            <DialogContent data-testid="upgrade-plan-dialog" className="sm:max-w-[520px]">
                <DialogHeader>
                    <div className="h-11 w-11 rounded-sm bg-primary/10 flex items-center justify-center mb-2">
                        <Rocket className="h-5 w-5 text-primary" />
                    </div>
                    <DialogTitle className="text-xl">Ready to unlock Enterprise?</DialogTitle>
                    <DialogDescription>
                        You're on <strong>{currentPlanName}</strong>. Upgrade to Enterprise and scale without limits.
                    </DialogDescription>
                </DialogHeader>

                <ul className="space-y-2.5 py-2" data-testid="upgrade-plan-benefits">
                    {BENEFITS.map((b) => (
                        <li key={b} className="flex items-start gap-2.5 text-sm">
                            <span className="mt-0.5 h-4 w-4 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                                <Check className="h-2.5 w-2.5 text-primary" />
                            </span>
                            {b}
                        </li>
                    ))}
                </ul>

                <div className="rounded-sm border border-border bg-muted/30 p-3">
                    <Label htmlFor="upgrade-note" className="text-xs mb-1.5 block">Anything you'd like us to know? (optional)</Label>
                    <Textarea
                        id="upgrade-note"
                        placeholder="Team size, use case, timeline…"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={2}
                        data-testid="upgrade-plan-note-input"
                    />
                </div>

                {/* Terms & Privacy — informational text (no consent checkbox). */}
                <p className="text-[12px] text-muted-foreground leading-snug mt-1" data-testid="upgrade-terms-text">
                    By submitting this request, you agree to the Analyzio{" "}
                    <a href={linkHref(legalLinks?.terms, "/terms")} target="_blank" rel="noreferrer"
                        className="text-primary underline hover:text-primary/80" data-testid="upgrade-terms-link">Terms</a>
                    {" "}and{" "}
                    <a href={linkHref(legalLinks?.privacy, "/privacy")} target="_blank" rel="noreferrer"
                        className="text-primary underline hover:text-primary/80" data-testid="upgrade-privacy-link">Privacy Policy</a>.
                </p>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="upgrade-plan-cancel">
                        Maybe later
                    </Button>
                    <Button onClick={submit} disabled={submitting} data-testid="upgrade-plan-submit">
                        {submitting ? "Sending…" : "Request upgrade"}
                        <ArrowUpRight className="h-4 w-4 ml-1" />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default UpgradePlanDialog;
